import React, {
  createContext,
  forwardRef,
  PropsWithChildren,
  ReactElement,
  useContext,
} from 'react';
import logo from '../images/linz-logo-col-n-white-txt.svg';
import motif from '../images/linz-motif.svg';
import clsx from 'clsx';
import { useEscapeFunction, usePageClickFunction } from './common/Hooks';
import { buildHideClassDict, Range } from './common/ResponsiveUtils';

type HeaderSize = 'normal' | 'small';

interface HeaderProps {
  headingText?: string;
  size?: HeaderSize;
  /** The URL or path that the LINZ logo will link to. If unset, the logo will not be a link. */
  homeLink?: string;
  transparent?: boolean;
  sticky?: boolean;
}

const LuiHeader: React.FC<HeaderProps> = ({
  headingText,
  size = 'small',
  homeLink,
  transparent,
  children,
  sticky,
}) => {
  let logoElement =
    size === 'normal' ? (
      <img className="linz-logo" alt="LINZ Logo" src={logo} />
    ) : (
      <img className="linz-motif" alt="LINZ Logo" src={motif} />
    );
  return (
    <header
      className={clsx({
        'lui-header': true,
        'lui-header-transparent': transparent,
        'lui-header-small': size === 'small',
        'lui-header-sticky': sticky,
      })}
    >
      <div className="lui-header-row">
        <div className="lui-header-col">
          <div className="lui-header-logo">
            {homeLink ? <a href={homeLink}>{logoElement}</a> : logoElement}
          </div>
          {headingText && (
            <div className="lui-header-title">
              <h1>{headingText}</h1>
            </div>
          )}
        </div>
        <div className="lui-header-col">{children}</div>
      </div>
    </header>
  );
};

interface ILuiIcon {
  /**
   * The material-ui icon name (see https://material.io/resources/icons/?search=account_c&style=round)
   */
  icon?: string;

  /**
   * Hides the menu item on screens specified
   */
  hide?: Range;
}

interface ILuiHeaderMenuItem extends ILuiIcon {
  /**
   * The label that will show up beside (right) the icon, if not specified then it won't show up
   */
  label?: string | ReactElement;

  /**
   * The string that will show up as a notification badge which will show up at the upper right corner of the icon.
   */
  badge?: string;

  /**
   * The callback handler to be registered which will be called when menu item is clicked.
   */
  onClick?: () => void;

  /**
   * The test id used to reference the html element which is bound to the onClick function.
   */
  'data-testid'?: string;
}

/**
 * The <code>LuiHeaderMenuItem</code> is the base component for all menu items that are to be shown in the header.
 * Note that it depends on it being rendered inside a LuiHeader for its styles to apply properly.
 * To configure the item for responsiveness, ensure to set any of the hideOn* properties.
 */
const LuiHeaderMenuItem = forwardRef<
  HTMLDivElement,
  PropsWithChildren<ILuiHeaderMenuItem>
>(
  (
    {
      icon,
      label,
      badge,
      hide,
      onClick,
      'data-testid': dataTestId = 'menu-item',
      children,
    },
    ref
  ) => {
    const menuItemClasses = clsx(
      'lui-header-menu-item',
      hide && buildHideClassDict(hide)
    );
    const resolvedIcon = !icon && !label ? 'menu' : icon;
    return (
      <div className={menuItemClasses} ref={ref}>
        <div
          className="lui-header-menu-icon"
          onClick={onClick}
          data-testid={dataTestId}
        >
          <i className="material-icons-round md-36">{resolvedIcon}</i>
          {label && <div className="lui-menu-label">{label}</div>}
          {badge && <div className="badge">{badge}</div>}
        </div>
        {children}
      </div>
    );
  }
);

interface ILuiCloseableHeaderMenuContext {
  isOpen: () => boolean;
  open: () => void;
  close: () => void;
}

const LuiCloseableHeaderMenuContext = createContext<
  ILuiCloseableHeaderMenuContext
>({
  isOpen: () => false,
  open: () => {},
  close: () => {},
});

interface ILuiCloseableHeaderMenuItem
  extends Omit<ILuiHeaderMenuItem, 'onClick'> {
  open: boolean;
  setOpen: (value: boolean) => void;
}

const LuiCloseableHeaderMenuItem: React.FC<ILuiCloseableHeaderMenuItem> = ({
  open,
  setOpen,
  ...props
}) => {
  useEscapeFunction(() => setOpen(false));
  const menuDiv = usePageClickFunction<HTMLDivElement>(
    (event) => open || event.stopPropagation(),
    (event) => {
      if (!open) {
        event.stopPropagation();
        setOpen(false);
      }
    }
  );
  const menuControls = {
    isOpen: () => open,
    open: () => setOpen(true),
    close: () => setOpen(false),
  };
  const menuItemProps = { ...props, onClick: () => menuControls.open() };
  return (
    <LuiCloseableHeaderMenuContext.Provider value={menuControls}>
      <LuiHeaderMenuItem ref={menuDiv} {...menuItemProps}>
        {props.children}
      </LuiHeaderMenuItem>
    </LuiCloseableHeaderMenuContext.Provider>
  );
};

interface ILuiMenuCloseButton extends ILuiIcon {
  'data-testid'?: string;
}

const LuiMenuCloseButton: React.FC<ILuiMenuCloseButton> = ({
  icon = 'close',
  hide,
  'data-testid': dataTestId = 'close-button',
}) => {
  const menu = useContext(LuiCloseableHeaderMenuContext);
  const classes = clsx(
    'material-icons-round',
    'lui-menu-close-button',
    hide && buildHideClassDict(hide)
  );
  return (
    <i className={classes} onClick={menu.close} data-testid={dataTestId}>
      {icon}
    </i>
  );
};

export {
  HeaderSize,
  HeaderProps,
  LuiHeader,
  ILuiIcon,
  ILuiHeaderMenuItem,
  LuiHeaderMenuItem,
  ILuiMenuCloseButton,
  LuiMenuCloseButton,
  ILuiCloseableHeaderMenuItem,
  LuiCloseableHeaderMenuItem,
  LuiCloseableHeaderMenuContext,
};
